/**
 * useHoldRepeat — press-and-hold key-repeat behavior for toolbar buttons.
 *
 * Matches the OS-level keyboard repeat convention: a single tap fires the
 * action once (via the native `click` event, preserving keyboard/assistive
 * accessibility). Holding past `initialDelayMs` kicks off rapid iteration at
 * `intervalMs`. Releasing cancels both the pending delay and the active
 * interval. The trailing `click` that follows a release after the repeat has
 * engaged is suppressed so the hold doesn't produce one final extra action.
 */

import { useCallback, useEffect, useRef } from "react";

export interface HoldRepeatOptions {
  /** Time (ms) the pointer must be held before rapid iteration begins. */
  initialDelayMs?: number;
  /** Interval (ms) between iterations once rapid iteration has started. */
  intervalMs?: number;
}

export interface HoldRepeatHandlers {
  onClick: () => void;
  onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLButtonElement>) => void;
}

export function useHoldRepeat(
  action: () => void,
  enabled: boolean,
  options: HoldRepeatOptions = {},
): HoldRepeatHandlers {
  const { initialDelayMs = 400, intervalMs = 60 } = options;

  const actionRef = useRef(action);
  actionRef.current = action;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const repeatedRef = useRef(false);

  const stop = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      // Capture so pointerup reaches us even if the pointer drifts off the
      // button — otherwise a moved-off release would leak the interval.
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Pointer capture is best-effort.
      }
      stop();
      repeatedRef.current = false;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (!enabledRef.current) return;
        repeatedRef.current = true;
        actionRef.current();
        intervalRef.current = setInterval(() => {
          if (!enabledRef.current) {
            stop();
            return;
          }
          actionRef.current();
        }, intervalMs);
      }, initialDelayMs);
    },
    [initialDelayMs, intervalMs, stop],
  );

  const onPointerUp = useCallback(() => {
    stop();
  }, [stop]);

  const onPointerCancel = useCallback(() => {
    stop();
    // A cancelled gesture shouldn't produce a trailing click.
    repeatedRef.current = true;
  }, [stop]);

  const onClick = useCallback(() => {
    if (repeatedRef.current) {
      repeatedRef.current = false;
      return;
    }
    if (!enabledRef.current) return;
    actionRef.current();
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { onClick, onPointerDown, onPointerUp, onPointerCancel };
}
